import { atom } from "jotai";
import { atomWithInfiniteQuery } from "jotai-tanstack-query";
import { fetchNotifications } from "../../apis";
import { NotificationsResponse } from "../../types";
import { authAtom } from "../auth/atoms";

/**
 * Filter state for notifications
 * Stores current filter parameters for the notification queries
 */
export interface NotificationFilters {
  notification_type: "live_alert" | "accessibility_information" | "all";
  unread_only: boolean;
  search?: string;
}

/**
 * Factory function to create a notifications atom with specific filters
 * This allows multiple components to have independent notification queries
 * Each combination of filters will be cached separately by TanStack Query
 */
export const createNotificationsAtom = (
  notification_type: "live_alert" | "accessibility_information" | "all" = "all",
  unread_only: boolean = false,
  search?: string
) => {
  return atomWithInfiniteQuery((get) => {
    const auth = get(authAtom);
    const user = auth.data;

    return {
      queryKey: [
        "notifications",
        user?.id,
        notification_type,
        unread_only,
        search,
      ],
      queryFn: async ({ pageParam }: { pageParam: number }) => {
        const token = user?.accessToken || "";
        const page = pageParam;
        const unreadOnlyParam = unread_only ? "yes" : "no";
        return fetchNotifications(
          token,
          page,
          20,
          notification_type,
          unreadOnlyParam,
          search
        );
      },
      getNextPageParam: (lastPage: NotificationsResponse) => {
        // Add safety check for pagination
        if (!lastPage?.pagination) {
          return undefined;
        }

        const { current_page, last_page } = lastPage.pagination;
        if (current_page < last_page) {
          return current_page + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      // Only run this query if the user is logged in
      enabled: !!user,
      retry: 1,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 5, // 5 minutes - notifications can be slightly stale
    };
  });
};
